import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  researchCoverageService,
  isActiveResearchCoverageJobStatus,
} from '@/services/researchCoverageService';
import type {
  ResearchJobStatus,
  ResearchTriggerJobRequest,
  ResearchCoverageJobListParams,
} from '@/services/researchCoverageService';

const ACTIVE_JOB_POLL_MS = 5_000;
const IDLE_JOB_POLL_MS = 30_000;

function hasActiveResearchJob(data?: { jobs?: { status: ResearchJobStatus }[] }) {
  return data?.jobs?.some((job) => isActiveResearchCoverageJobStatus(job.status)) ?? false;
}

export function useResearchCoverageOverview(tenantId: string) {
  return useQuery({
    queryKey: ['research-coverage', 'overview', tenantId],
    queryFn: () => researchCoverageService.getOverview(tenantId),
    enabled: !!tenantId,
    staleTime: 30_000,
    refetchInterval: 30_000,
    retry: 1,
  });
}

export function useResearchCoveragePools(tenantId: string) {
  return useQuery({
    queryKey: ['research-coverage', 'pools', tenantId],
    queryFn: () => researchCoverageService.getPools(tenantId),
    enabled: !!tenantId,
    staleTime: 30_000,
    refetchInterval: 30_000,
    retry: 1,
  });
}

export function useResearchCoverageImports(tenantId: string, limit = 50) {
  return useQuery({
    queryKey: ['research-coverage', 'imports', tenantId, limit],
    queryFn: () => researchCoverageService.getImports(tenantId, limit),
    enabled: !!tenantId,
    staleTime: 30_000,
    refetchInterval: 30_000,
    retry: 1,
  });
}

export function useResearchCoverageReadiness(tenantId: string, keyword: string) {
  return useQuery({
    queryKey: ['research-coverage', 'readiness', tenantId, keyword],
    queryFn: () => researchCoverageService.getReadiness(tenantId, keyword),
    enabled: !!tenantId && !!keyword,
    staleTime: 60_000,
    retry: 1,
  });
}

export function useResearchCoverageLaneStatus() {
  return useQuery({
    queryKey: ['research-coverage', 'lane-status'],
    queryFn: () => researchCoverageService.getLaneStatus(),
    staleTime: 30_000,
    refetchInterval: 30_000,
    retry: 1,
  });
}

export function useResearchCoverageJobs(params: ResearchCoverageJobListParams) {
  return useQuery({
    queryKey: ['research-coverage', 'jobs', params],
    queryFn: () => researchCoverageService.listJobs(params),
    enabled: !!params?.tenant_id,
    staleTime: ACTIVE_JOB_POLL_MS,
    refetchInterval: (query) =>
      hasActiveResearchJob(query.state.data) ? ACTIVE_JOB_POLL_MS : IDLE_JOB_POLL_MS,
    retry: 1,
  });
}

export function useResearchCoverageJob(jobId: string | null, tenantId: string, enabled = true) {
  return useQuery({
    queryKey: ['research-coverage', 'job', jobId, tenantId],
    queryFn: () => researchCoverageService.getJob(jobId!, tenantId),
    enabled: enabled && !!jobId && !!tenantId,
    staleTime: ACTIVE_JOB_POLL_MS,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && isActiveResearchCoverageJobStatus(status)
        ? ACTIVE_JOB_POLL_MS
        : IDLE_JOB_POLL_MS;
    },
    retry: 1,
  });
}

export function useTriggerResearchCoverageJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      request,
      idempotencyKey,
    }: {
      request: ResearchTriggerJobRequest;
      idempotencyKey?: string;
    }) => researchCoverageService.triggerJob(request, idempotencyKey),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['research-coverage', 'jobs'] });
      queryClient.invalidateQueries({ queryKey: ['research-coverage', 'job', data.job_id] });
      queryClient.invalidateQueries({ queryKey: ['research-coverage', 'overview'] });
      queryClient.invalidateQueries({ queryKey: ['research-coverage', 'pools'] });
      queryClient.invalidateQueries({ queryKey: ['research-coverage', 'imports'] });
    },
  });
}

export function useCancelResearchCoverageJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ jobId, tenantId }: { jobId: string; tenantId: string }) =>
      researchCoverageService.cancelJob(jobId, tenantId),
    onSuccess: (data, { jobId, tenantId }) => {
      queryClient.invalidateQueries({ queryKey: ['research-coverage', 'jobs'] });
      queryClient.invalidateQueries({ queryKey: ['research-coverage', 'job', jobId, tenantId] });
      if (data.job_id !== jobId) {
        queryClient.invalidateQueries({
          queryKey: ['research-coverage', 'job', data.job_id, tenantId],
        });
      }
    },
  });
}
